/**
 * RubyLingo — "ô hình" cho 46 từ KHÔNG THỂ có ảnh chụp: 26 chữ cái + 20 số.
 *
 * ⚠️⚠️ VÌ SAO CẦN TỆP NÀY (đọc trước khi "đơn giản hoá" bằng emoji):
 *
 *   Luật sản phẩm: Lật thẻ ghi nhớ CHỈ ghép **từ tiếng Anh ↔ HÌNH**. Không bao giờ dùng nghĩa
 *   tiếng Việt làm một mặt thẻ — bé 7 tuổi chưa đọc thạo, và trò này dạy liên kết từ–hình.
 *   (Xem `MemoryMatchConfig.pairMode` — kiểu dữ liệu chỉ còn đúng một giá trị.)
 *
 *   Nhưng 46 từ dưới đây khai `picturable: false` và KHÔNG có tệp ảnh, vì chúng KHÔNG phải đồ
 *   vật: "A" không phải con vật, "twelve" không phải cái ghế. Vẽ một con voi cho chữ "E" là làm
 *   hỏng bài học. Nội dung của chúng CHÍNH LÀ hình dạng ký tự và SỐ LƯỢNG.
 *
 *   ⇒ Ô hình ở đây là hình vẽ THẬT (SVG nội tuyến, không tải mạng):
 *       • chữ cái : chữ THƯỜNG cỡ lớn ("a", "b"…) trên nền ô bo tròn có gradient nhẹ;
 *       • số      : chữ số lớn + ĐÚNG bằng ấy chấm tròn (one = 1 chấm … twenty = 20 chấm).
 *
 * ⚠️⚠️ CHỮ CÁI PHẢI LÀ CHỮ THƯỜNG — ĐÂY LÀ RÀNG BUỘC SƯ PHẠM, KHÔNG PHẢI THẨM MỸ:
 *   Thẻ CHỮ của từ `starters.a` hiện `Word.en` = "A" (chữ HOA). Nếu ô hình cũng vẽ "A" thì hai
 *   thẻ của cùng một cặp GIỐNG HỆT NHAU ⇒ trò chơi biến thành "tìm hai thẻ giống nhau", mất hẳn
 *   phần dạy học. Vẽ chữ thường thì cặp "A" ↔ "a" vừa KHÁC nội dung (ghép cặp còn nghĩa), vừa
 *   dạy đúng thứ bé cần: chữ hoa và chữ thường là hai hình dạng của cùng một chữ cái.
 *
 * ⚠️ SỐ CHẤM PHẢI ĐẾM ĐÚNG TUYỆT ĐỐI. Đây là bài học về SỐ LƯỢNG: "seven" mà vẽ 6 hay 8 chấm là
 *   dạy sai, và bé 7 tuổi tin hình hơn tin lời. Vì vậy `count` KHÔNG viết tay từng số một mà
 *   suy từ THỨ TỰ của `NUMBER_WORDS` (index + 1), và `tests/unit/client/word-glyph.test.tsx`
 *   đếm lại số phần tử `<circle>` của từng ô.
 *
 * ⭐ VÌ SAO Ở `shared/` CHỨ KHÔNG PHẢI `src/lib/`: `scripts/validate-content.ts` (chạy bằng
 *   `tsx`, `tsconfig.server.json` chỉ gom `server/ shared/ scripts/`) phải đọc được registry này
 *   để luật V11 "game dạng hình cần đủ thứ để nhìn" tính đúng cho `alphabet`/`numbers-1-20`.
 *   Để ở `src/` thì script không import được, và hai bên sẽ có hai định nghĩa "từ có hình" —
 *   đúng kiểu lệch đã trả giá ở `word-assets.json` ↔ V11.
 *   Tệp này THUẦN DỮ LIỆU: không DOM, không React. Component vẽ nằm ở
 *   `src/components/common/WordGlyph.tsx`.
 */

/** Một ô hình thay cho ảnh chụp. */
export type WordGlyph =
  /** Chữ cái: `letter` là CHỮ THƯỜNG một ký tự ("a"…"z"). */
  | { readonly kind: 'letter'; readonly letter: string }
  /** Số: `count` là số lượng cần vẽ — cũng là số chấm tròn. */
  | { readonly kind: 'number'; readonly count: number };

/**
 * 26 chữ cái, ĐÚNG thứ tự bảng chữ cái.
 * ⚠️ Giữ nguyên chuỗi một dòng: `starters.<chữ>` là quy ước id của level (`starters.a`…`starters.z`).
 */
const LETTERS = 'abcdefghijklmnopqrstuvwxyz';

/**
 * 20 từ chỉ số, ĐÚNG thứ tự 1…20.
 *
 * ⚠️ THỨ TỰ LÀ DỮ LIỆU, KHÔNG phải trang trí: `count` = index + 1. Đổi thứ tự (hoặc chèn một từ
 *    vào giữa) là mọi ô số sau đó đếm sai — mà nhìn bằng mắt thì ô nào cũng "có chấm", không ai
 *    phát hiện. Test đối chiếu từng `word.en` với chính chuỗi ở đây nên sai thứ tự là CI đỏ.
 */
const NUMBER_WORDS = [
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
  'twenty',
] as const;

function buildRegistry(): ReadonlyMap<string, WordGlyph> {
  const map = new Map<string, WordGlyph>();
  for (const letter of LETTERS) {
    // id từ vựng = `{levelCode}.{slug(en)}`; slug của chữ cái là chính chữ đó, viết thường.
    map.set(`starters.${letter}`, { kind: 'letter', letter });
  }
  NUMBER_WORDS.forEach((word, index) => {
    map.set(`starters.${word}`, { kind: 'number', count: index + 1 });
  });
  return map;
}

const GLYPHS = buildRegistry();

/** Id của MỌI từ có ô hình — đúng 46 (26 chữ cái + 20 số). Dùng cho test và validator. */
export const WORD_GLYPH_IDS: readonly string[] = [...GLYPHS.keys()];

/**
 * Ô hình của một từ, hoặc `null` nếu từ đó không thuộc nhóm chữ cái/số.
 *
 * ⚠️ Tra theo `wordId` (KHÔNG theo `word.icon`): `icon` của 46 từ này tình cờ cũng là "A"/"12",
 *   nhưng `icon` là trường hiển thị dự phòng và có thể bị đổi khi làm lại nội dung. `wordId` mới
 *   là khoá ổn định, và cũng là thứ `WordIcon` đã nhận sẵn.
 */
export function wordGlyphOrNull(wordId: string): WordGlyph | null {
  return GLYPHS.get(wordId) ?? null;
}
